/**
 * User Templates Tab
 * Displays user-created/saved email templates
 *
 * @file src/components/email/templates/UserTemplatesTab.tsx
 * @created 2025-10-22
 */

import React, { useEffect, useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import TemplateGrid from './TemplateGrid';
import EmailService, { EmailTemplate } from '@/api/email';
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react';

interface UserTemplatesTabProps {
  organisationId: string;
  onTemplateSelect: (designJson: Record<string, any>, templateId: string) => void;
}

/**
 * UserTemplatesTab Component
 *
 * Features:
 * - Fetches user/org templates from API
 * - Direct template loading (no customization needed)
 * - Refresh button to reload templates
 * - Error handling and loading states
 *
 * @example
 * <UserTemplatesTab
 *   organisationId="org-123"
 *   onTemplateSelect={(designJson, templateId) => {
 *     editor.loadProjectData(designJson);
 *   }}
 * />
 */
const UserTemplatesTab: React.FC<UserTemplatesTabProps> = ({
  organisationId,
  onTemplateSelect
}) => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchUserTemplates();
  }, [organisationId]);

  const fetchUserTemplates = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch templates for this organisation (non-system templates)
      const response = await EmailService.fetchTemplates(organisationId, {
        // Note: Backend should support filtering by is_system_template
        // For now, we'll fetch all and filter client-side
      });

      // Filter for user templates only (non-system)
      const userTemplates = response.filter(t => !t.is_system_template);

      setTemplates(userTemplates);
    } catch (error: any) {
      console.error('Error fetching user templates:', error);
      setError(error.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateClick = (template: EmailTemplate) => {
    // User templates load directly without customization
    if (template.design_json) {
      onTemplateSelect(template.design_json, template.id);
    } else {
      // Fallback: if no design_json, we can't load the template
      console.error('Template missing design_json:', template.id);
      setError('This template cannot be loaded (missing design data)');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-gray-600">Loading your templates...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error}
          </AlertDescription>
        </Alert>
        <div className="flex justify-center">
          <Button onClick={fetchUserTemplates} variant="outline" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Refresh Button */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Your Saved Templates</h3>
          <p className="text-sm text-gray-600">
            Templates you've created and saved for reuse
          </p>
        </div>
        <Button
          onClick={fetchUserTemplates}
          variant="outline"
          size="sm"
          className="gap-2"
          disabled={loading}
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Template Grid */}
      <TemplateGrid
        templates={templates}
        onTemplateClick={handleTemplateClick}
        showCustomizeButton={false}
        emptyMessage="No saved templates yet"
      />

      {/* Helpful Tip */}
      {templates.length === 0 && !loading && (
        <div className="mt-8 p-4 bg-blue-50 rounded-md border border-blue-200">
          <div className="flex items-start gap-2">
            <div className="text-blue-500 mt-0.5">💡</div>
            <div>
              <div className="font-medium text-sm text-blue-700">
                How to Save Templates
              </div>
              <p className="text-xs text-blue-600 mt-1">
                When creating an email campaign, click the "Save as Template" button in Step 3 (Email Content)
                to save your design for future use. Your saved templates will appear here.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserTemplatesTab;
